import React, {useRef} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import {Project} from '../../../../data/models/Projects/Project';
import {CourseListItem} from '../../../../data/models/Course/Course';
import {MicroreelGroup} from '../../../../data/models/Microreels/MicroreelGroup';
import {GRADABLE_COURSE_TYPE} from '../../../../data/staticValues/courseStructure';
import {logoTextWhite} from '../../../../assets/images';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import NewCourseCard from '../../../Courses/List/components/CourseCard';
import MicroreelGroupCard from '../../../Microreels/List/components/MicroreelGroupCard';
import PostCard from '../../../Posts/List/components/PostCard';
import PostsEmptyState from '../../../../uiToolkit/EmptyStates/PostsEmptyState';
import {PostListItem} from '../../../../data/models/Posts/Post';
import ProjectContentToggle, {
  ProjectTab,
} from '../../components/ProjectContentToggle';
import Button from '../../../../uiToolkit/Buttons/Button';
import ProjectsEmptyState from '../../../../uiToolkit/EmptyStates/ProjectsEmptyState';
import MicroreelsEmptyState from '../../../../uiToolkit/EmptyStates/MicroreelsEmptyState';
import {projectsText} from '../../projectsText';
import '../../../Courses/List/view/styles.css';
import styles from './ProjectDetailsUI.module.css';

interface Props {
  project?: Project;
  canManage: boolean;
  permissions: string[];
  tab: ProjectTab;
  onChangeTab: (tab: ProjectTab) => void;
  courses: CourseListItem[];
  coursesLoaded: boolean;
  groups: MicroreelGroup[];
  groupsLoaded: boolean;
  onBack: () => void;
  onRename: () => void;
  onDelete: () => void;
  onCreateCourse: () => void;
  onCreateMicroreels: () => void;
  onCreatePost: () => void;
  loadNextPage: () => Promise<void>;
  onEditCourse: (courseId: string) => void;
  onOpenCourseFolders: (courseId: string) => void;
  onPreviewCourse: (courseId: string) => void;
  onCopyCourse: (courseId: string) => void;
  onDeleteCourse: (courseId: string) => void;
  onMoveCourse: (courseId: string) => void;
  onOpenGroup: (group: MicroreelGroup) => void;
  onGroupCourseEditor: (group: MicroreelGroup) => void;
  onEditGroup: (group: MicroreelGroup) => void;
  onDeleteGroup: (group: MicroreelGroup) => void;
  onGroupAnalytics: (group: MicroreelGroup) => void;
  onMoveGroup: (group: MicroreelGroup) => void;
  posts: PostListItem[];
  postsLoaded: boolean;
  onOpenPost: (post: PostListItem) => void;
  onEditPost: (post: PostListItem) => void;
  onPostLearners: (post: PostListItem) => void;
  onPostAnalytics: (post: PostListItem) => void;
  onDeletePost: (post: PostListItem) => void;
  onMovePost: (post: PostListItem) => void;
}

const ProjectDetailsUI = React.memo(
  ({
    project,
    canManage,
    permissions,
    tab,
    onChangeTab,
    courses,
    coursesLoaded,
    groups,
    groupsLoaded,
    onBack,
    onRename,
    onDelete,
    onCreateCourse,
    onCreateMicroreels,
    onCreatePost,
    loadNextPage,
    onEditCourse,
    onOpenCourseFolders,
    onPreviewCourse,
    onCopyCourse,
    onDeleteCourse,
    onMoveCourse,
    onOpenGroup,
    onGroupCourseEditor,
    onEditGroup,
    onDeleteGroup,
    onGroupAnalytics,
    onMoveGroup,
    posts,
    postsLoaded,
    onOpenPost,
    onEditPost,
    onPostLearners,
    onPostAnalytics,
    onDeletePost,
    onMovePost,
  }: Props) => {
    const observer = useRef<any>();
    const overscrollLock = useRef(false);

    const lastItemRef = (node: any) => {
      if (overscrollLock.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    if (!project) {
      return (
        <div className={styles.loading}>
          <LoadingSpinner size={48} />
        </div>
      );
    }

    const renderSectionLoading = () => (
      <div className={styles.sectionLoading}>
        <LoadingSpinner size={32} />
      </div>
    );

    return (
      <div className={styles.container}>
        <button className={styles.backLink} onClick={onBack}>
          ← {projectsText.allProjects}
        </button>
        <div className={styles.headerRow}>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{project.name}</h1>
            <p className={styles.subtitle}>
              {projectsText.itemsSummary(project)}
            </p>
          </div>
          {canManage && (
            <div className={styles.actions}>
              <button className={styles.textAction} onClick={onRename}>
                {projectsText.edit}
              </button>
              <button className={styles.textAction} onClick={onDelete}>
                {projectsText.delete}
              </button>
            </div>
          )}
        </div>

        <div className={styles.toggleRow}>
          <ProjectContentToggle tab={tab} onChange={onChangeTab} />
          {canManage && tab === 'courses' && (
            <Button
              title={projectsText.newCourse}
              onClick={onCreateCourse}
              uiType="action"
              icon="icon-plus"
            />
          )}
          {canManage && tab === 'microreels' && (
            <Button
              title={projectsText.createMicroreels}
              onClick={onCreateMicroreels}
              uiType="action"
              icon="icon-plus"
            />
          )}
          {canManage && tab === 'posts' && (
            <Button
              title={projectsText.newPost}
              onClick={onCreatePost}
              uiType="action"
              icon="icon-plus"
            />
          )}
        </div>
        {tab === 'courses' ? (
          <>
            {!coursesLoaded ? (
              renderSectionLoading()
            ) : courses.length ? (
              <div className={`CoursesListContainer ${styles.coursesGrid}`}>
                <ul className="row CourseCardContainer">
                  {courses.map(course => (
                    <NewCourseCard
                      key={course.id}
                      course={course}
                      courseId={course.id}
                      courseImage={course.image}
                      logoTextWhite={logoTextWhite}
                      courseTitle={course.title}
                      courseSubjecttitle={course.subjectTitle}
                      coursePublished={course.published}
                      courseIsPublic={course.isPublic}
                      courseGradable={
                        course.type.toString() === GRADABLE_COURSE_TYPE
                      }
                      courseAverageGrade={course.averageGrade}
                      courseEnrollNumber={course.enrollNumber}
                      courseLearners={course.enrollNumber}
                      onOpenFolders={() => onOpenCourseFolders(course.id)}
                      onPreview={() => onPreviewCourse(course.id)}
                      onCopyCourse={() => onCopyCourse(course.id)}
                      onDelete={() => onDeleteCourse(course.id)}
                      onEdit={() => onEditCourse(course.id)}
                      onMoveToProject={() => onMoveCourse(course.id)}
                      hideProject
                      permissions={permissions}
                    />
                  ))}
                </ul>
                <div ref={lastItemRef} />
              </div>
            ) : (
              <ProjectsEmptyState
                icon="course-menu.svg"
                title={projectsText.emptyCourses}
                subtitle={projectsText.emptyCoursesSubtitle}
              />
            )}
          </>
        ) : tab === 'microreels' ? (
          <>
            {!groupsLoaded ? (
              renderSectionLoading()
            ) : groups.length ? (
              <Container fluid className={styles.list}>
                <Row>
                  {groups.map(group => (
                    <Col xl={3} lg={4} key={group.id} className={styles.col}>
                      <MicroreelGroupCard
                        group={group}
                        onOpen={onOpenGroup}
                        onCourseEditor={onGroupCourseEditor}
                        onEditInfo={onEditGroup}
                        onDelete={onDeleteGroup}
                        onLearnerProgress={onGroupAnalytics}
                        onMoveToProject={onMoveGroup}
                        hideProject
                      />
                    </Col>
                  ))}
                </Row>
                <div ref={lastItemRef} />
              </Container>
            ) : (
              <MicroreelsEmptyState
                title={projectsText.emptyMicroreels}
                subtitle={projectsText.emptyMicroreelsSubtitle}
              />
            )}
          </>
        ) : (
          <>
            {!postsLoaded ? (
              renderSectionLoading()
            ) : posts.length ? (
              <Container fluid className={styles.list}>
                <Row>
                  {posts.map(post => (
                    <Col xl={3} lg={4} key={post.id} className={styles.col}>
                      <PostCard
                        post={post}
                        onOpen={onOpenPost}
                        onEditInfo={onEditPost}
                        onLearners={onPostLearners}
                        onLearnerProgress={onPostAnalytics}
                        onDelete={onDeletePost}
                        onMoveToProject={onMovePost}
                        hideProject
                      />
                    </Col>
                  ))}
                </Row>
                <div ref={lastItemRef} />
              </Container>
            ) : (
              <PostsEmptyState
                title={projectsText.emptyPosts}
                subtitle={projectsText.emptyPostsSubtitle}
              />
            )}
          </>
        )}
      </div>
    );
  },
);

export default ProjectDetailsUI;
